/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
// libraries
import { Box, Typography, Stack, Tooltip, Zoom, Collapse, IconButton, Chip } from '@mui/material';
import { isEmpty } from 'lodash';
import { piiProps } from 'src/components/DemoBlur';
import { resolveAppointmentOutcomeSelection } from '../../utils/appointmentOutcomeSelection';
// components
import FollowUpChecklistV2 from '../../sections/@dashboard/leadDetail/FollowUpChecklistV2';
import AttendanceGate from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/AttendanceGate';
import useGuidedChecklist from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/hooks/useGuidedChecklist';
import { hasFeatureAccess } from '../../utils/featurePermission';
import Iconify from '../Iconify';
import { NewAppointment, TreatmentComplete, Archive } from '../tabs';
import { AssignUserAvatar } from '../custom-avatar';
// store
import { dispatch, useSelector, store } from '../../redux/store';
import { saveNewLeadDetail, CompleteTreatment, attenedAppoimentNotes, RecordAttended, tableLead } from '../../redux/slices/lead';
import { beginArchiveLead } from '../../redux/slices/lead/coreThunks';
import { updateError } from '../../redux/slices/user';
// context/utils/constant/hooks
import { useSettingsContext } from '../settings/context';
import capitalizeFirstLetter from '../../utils/capitalizedFunc';
import { getTreatmentDate } from '../../utils/multiTreatmentFlow';
import { isDisabledTabForFinishButton } from '../../utils/clinCheckUtils';
import { useTabs } from '../../hooks';
// style
import TCOScript from './TCOScript';
import AssignedUsersShow from '../AssignedUsersShow';
import useReminderContent from './useReminderContent';
import ScheduleReminderTaskSection from '../../sections/@dashboard/leadDetail/ScheduleReminderTaskSection';
import useLeadReminderTasks from '../../sections/@dashboard/leadDetail/hooks/useLeadReminderTasks';
import AttendedPopupShell from './AttendedPopupShell';
import CompleteTreatmentConfirmDialog from './CompleteTreatmentConfirmDialog';
import resolveLeadTreatmentPrice from '../../utils/resolveLeadTreatmentPrice';
import ContactPopupDebugBox from '../fullScreenPopUps/ContactPopupDebugBox';

// Re-export under the legacy name so existing imports don't break during transition.
export const resolveAttendedTreatmentPrice = ({ followLead, filterStatus = 'CONSULTATION_ATTENDED' } = {}) =>
    resolveLeadTreatmentPrice({ followLead, filterStatus });

const AttendedAppointment = forwardRef(({ isOverview, type, id, handleClose, handleCloseAndReload, user, OpenAssignTask, editDetailHandler, handleReloadLeads, isFinishButtonAvailable, handleNoAttended: onNoAttended, onOpenSidebarTab, debugSource = 'unknown' }, ref) => {
    const attendedRef = useRef();
    const { currentTab } = useTabs('');
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { followLead, currentTreatmentData, treatmentLoading } = useSelector((state) => state.lead);
    const { practice } = useSelector((state) => state.practice);
    const settings = useSettingsContext();
    const [activeTab, setActiveTab] = useState('');
    // Footer "Treatment complete" confirmation dialog — treatment-close is
    // irreversible, so the footer click opens this confirm before the POST.
    const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
    const [completeSubmitting, setCompleteSubmitting] = useState(false);
    // Decouples "Finish is satisfied" from "an outcome chip is visibly selected".
    // V2 checklist activity sets this true WITHOUT setting activeTab, so the
    // "What's next?" picker stays hidden and the yellow Reminder & Follow-up card
    // isn't auto-highlighted while the checklist is already shown. The submit path
    // treats the effective outcome as 'Reminder & Follow-up' when this is true.
    const [taskActivityDone, setTaskActivityDone] = useState(false);
    // True once the AttendanceGate "Yes" click has dispatched RecordAttended — the
    // status change is the meaningful action, so Finish should be enabled even before
    // any reminder/checklist activity. Starts false so the gate step stays gated.
    const [attendanceRecorded, setAttendanceRecorded] = useState(false);
    // Bumped after RecordAttended lands so the guided checklist refetches with
    // bustCache=true — the BE only computes task due dates AFTER the lead flips
    // to CONSULTATION_ATTENDED, so the first fetch (pre-attendance) returns null
    // due_at on every task. Coalesced via increment so multiple flips in the
    // same tick still produce one effective refetch.
    const [checklistRefreshKey, setChecklistRefreshKey] = useState(0);
    // When a guided checklist is present, the custom reminder form stays collapsed
    // behind an "Add custom reminder" trigger so it doesn't compete with the checklist.
    const [showCustomReminder, setShowCustomReminder] = useState(false);
    // Effective outcome for the submit/Finish gate: an explicitly-clicked chip
    // wins; otherwise checklist activity OR a recorded attendance implies the
    // Reminder & Follow-up outcome (falls through to a no-POST close — see thunk).
    const submitOutcome = activeTab || ((taskActivityDone || attendanceRecorded) ? 'Reminder & Follow-up' : null);
    const [depositPendingStatus, setDepositPendingStatus] = useState(false);
    const followLeadFilterAttend = followLead?.lead_appointment?.filter((el) => el.status === 'BOOKED' || el.status === 'CONSULTATION_BOOKED');

    const { isLoader } = useSelector((state) => state.loaderReducer);

    const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);
    const outcomeSelection = resolveAppointmentOutcomeSelection(practiceId, { ...user, treatment_id: user?.treatment_id ?? currentTreatmentDataGlobal?.treatmentData?.id }, followLead);
    const handleNoAttended = () => onNoAttended?.(outcomeSelection);


    const [assigneeSelected, setAssigneeSelected] = useState(followLead?.assignees || []);
    const [infoExpanded, setInfoExpanded] = useState(false);
    // Hoist isLeadBooked here so the gate-dismiss logic below can reuse it.
    const isLeadBooked = (followLead?.lead_treatments || user?.lead_treatments || []).some((t) => t.status === 'CONSULTATION_BOOKED');
    // Skip gate on re-open only when the lead is fully attended with no remaining
    // BOOKED treatment. For multi-treatment leads (e.g. Invisalign ATTENDED +
    // Dental Implants BOOKED) the gate must still show so the rep can record
    // attendance on the still-booked treatment.
    const hasAttendedTreatment = (followLead?.lead_treatments || user?.lead_treatments || []).some(
        (t) => t.status === 'CONSULTATION_ATTENDED'
    );
    const initiallyAttended = hasAttendedTreatment && !isLeadBooked;
    const [gateDismissed, setGateDismissed] = useState(initiallyAttended);

    const v2LeadId = followLead?.id || user?.lead_id || user?.id;
    const { initialLoading: gcInitialLoading, templatesEmpty } = useGuidedChecklist({ practiceId, leadId: v2LeadId, leadDetail: followLead });
    // Gate on initialLoading (not loading) so post-action refreshes don't unmount V2 mid-action
    // and discard its local state — parity with ConsultationAttendedAppointment.
    const v2GuidedActive = hasFeatureAccess('tasks-v2', practiceId) && !gcInitialLoading && !templatesEmpty;

    // Latch that tracks whether the guided checklist has ever resolved. Once true
    // it is never reset, so post-save/pause/complete refreshes that briefly flip
    // gcInitialLoading back to true do NOT re-show the loading skeleton — the
    // user is already interacting with the popup and the skeleton re-flash would
    // be jarring. The shell renders the skeleton only when isInitiallyLoading is
    // true, so latching here is the single gate that prevents re-flashing.
    const hasRevealedRef = useRef(false);
    if (!gcInitialLoading) hasRevealedRef.current = true;
    const isInitiallyLoading = !hasRevealedRef.current;

    // V2 guided checklist is now the DEFAULT experience for all practices.
    // Escape hatches (legacy v1): ?guidedV=1 in the URL, or a practice preference
    // guided_checklist_version === 'v1'. ?guidedV=2 always forces v2.
    const guidedParam = new URLSearchParams(window.location.search).get('guidedV');
    const practiceGuidedVersion = practice?.preferences?.guided_checklist_version;
    const useV2Guided = guidedParam === '2'
        || (guidedParam !== '1' && practiceGuidedVersion !== 'v1');

    // Debug panel toggle: on by default in local dev (import.meta.env.DEV).
    // Opt-out locally: ?attDebug=0 in the URL OR localStorage attDebug='0'.
    // In production (import.meta.env.DEV === false) only explicit opt-in activates it:
    //   ?attDebug=1 in the URL OR localStorage attDebug='1'.
    const attDebug =
        new URLSearchParams(window.location.search).get('attDebug') === '1'
        || (typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '1')
        || (import.meta.env.DEV
            && new URLSearchParams(window.location.search).get('attDebug') !== '0'
            && !(typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '0'));

    // While the attendance question is unanswered for a booked V2 lead, the gate
    // is the focal point — the rest of the popup body (full lead/treatment header)
    // is de-emphasised into a slim one-line summary.
    const gatePending = hasFeatureAccess('tasks-v2', practiceId) && isLeadBooked && !gateDismissed;

    // Single derived flow step — the one source of truth for every render gate.
    // Derived FROM the existing booleans (which remain for now). Phase 1 of the
    // shell refactor: unify AA & CAA gates on this so they stop diverging.
    const flowStep = (() => {
        if (gcInitialLoading) return 'LOADING';
        if (hasFeatureAccess('tasks-v2', practiceId) && isLeadBooked && !gateDismissed) return 'ATTENDANCE_GATE';
        if (activeTab) return 'OUTCOME_CHOSEN';
        return 'CONTENT';
    })();

    // ── Debug-only section outlines (behind attDebug; zero visual change otherwise) ──
    const dbgSx = (color) => (attDebug ? { outline: `2px dashed ${color}`, outlineOffset: '-2px', position: 'relative' } : null);
    const DbgTag = ({ color, label }) => (attDebug ? (
        <Box component="span" sx={{ position: 'absolute', top: 0, left: 0, zIndex: 50, bgcolor: color, color: '#fff', fontSize: '9px', fontWeight: 700, lineHeight: 1.4, px: 0.5, borderBottomRightRadius: 4, letterSpacing: '0.04em', pointerEvents: 'none' }}>{label}</Box>
    ) : null);
    DbgTag.propTypes = { color: PropTypes.string, label: PropTypes.string };

    useEffect(() => {
        if (type !== 'CreatedLead' && !isEmpty(currentTreatmentData)) {
            setDepositPendingStatus(currentTreatmentData.is_treatment_fee_pending);
        }
    }, [currentTreatmentData]);

    // When the popup opens for a lead that is ALREADY attended (initiallyAttended → gateDismissed
    // starts true, the gate never renders), default the outcome to Reminder & Follow-up so the
    // simplified picker reflects the in-progress reminder flow. Runs once on mount.
    useEffect(() => {
        if (initiallyAttended && !activeTab && hasFeatureAccess('tasks-v2', practiceId)) {
            setActiveTab('Reminder & Follow-up');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isPaymentMandatory = practice?.preferences?.is_payment_enforced_before_treatment;

    const defaultValues = {
        is_clinical_photographs_taken: false,
        is_oral_health_assessment_taken: false,
        is_xray_taken: false,
        is_digital_scan_taken: false,
        is_impressions_taken: false,
        is_treatment_options_discussed: false,
        is_treatment_plan_confirmed: false,
        is_finance_options_discussed: false,
        notes: ''
    };

    const methods = useForm({
        defaultValues,
    });

    const {
        handleSubmit
    } = methods;

    const appointmentNotesUpdate = async (noteData, callAppointmentNote) => {
        const noteDetail = {
            additional_notes: noteData.notes,
            clinical_attachments: [
                {
                    is_clinical_photographs_taken: noteData.is_clinical_photographs_taken,
                    is_oral_health_assessment_taken: noteData.is_oral_health_assessment_taken,
                    is_xray_taken: noteData.is_xray_taken
                }
            ],
            assessment_attachments: [
                {
                    is_digital_scan_taken: noteData.is_digital_scan_taken,
                    is_impressions_taken: noteData.is_impressions_taken,
                    is_treatment_options_discussed: noteData.is_treatment_options_discussed,
                    is_treatment_plan_confirmed: noteData.is_treatment_plan_confirmed,
                    is_finance_options_discussed: noteData.is_finance_options_discussed
                }
            ]
        };
        dispatch(saveNewLeadDetail({ attentendAppoiment: { noteDetail, activeTab: submitOutcome, callAppointmentNote } }));
        // ALWAYS complete/close. submit('finish') routes to the active outcome's
        // close path. attendedRef is attached to the active outcome tab's component
        // (NewAppointment / TreatmentComplete / Archive / TCOScript) — but the
        // "Reminder & Follow-up" outcome renders the V2 guided checklist (or the V1
        // FollowUpChecklistV2 fallback) which DOES NOT carry attendedRef. In that
        // case attendedRef.current.submit is undefined and the previous direct call
        // threw a swallowed TypeError → Finish was a silent no-op. Guard it and fall
        // back to handleClose so the popup always closes.
        if (typeof attendedRef?.current?.submit === 'function') {
            await attendedRef.current.submit('finish');
            // Archive tab stages archiveLead to Redux and sets markAsComplete=true but does
            // NOT dispatch the archive POST — it relies on a parent useEffect (LeadDetailTasks /
            // TaskReminder: followUpLead==='Attended', or NewLead/NHS: followUpLead==='recordAttended')
            // that IS mounted in those contexts. In the tasks-v2 sidebar (TaskBodyView), no such
            // effect is mounted. Detect the sidebar context precisely:
            //   1. submitOutcome === 'Archive'  — only Archive sets markAsComplete here
            //   2. markAsComplete === true       — Archive.onSubmit('finish') set the sentinel
            //   3. followUpLead === 'Attended'   — TaskBodyView dispatches 'Attended'; NewLead/NHS
            //                                      use 'recordAttended' and handle it themselves
            //   4. handleCloseAndReload provided — sidebar / table contexts; LeadDetailTasks omits it
            // All four conditions together uniquely identify the TaskBodyView sidebar path.
            // When AttendanceGate already recorded attendance (attendanceRecorded=true), skip the
            // redundant /attended/ call and go straight to attenedAppoimentNotes for the archive POST.
            if (
                submitOutcome === 'Archive' &&
                store.getState().lead?.markAsComplete &&
                store.getState().lead?.followUpLead === 'Attended' &&
                typeof handleCloseAndReload === 'function'
            ) {
                // RE-PRIME tableRowLead from the sidebar-scoped lead before dispatching.
                // RecordAttended and attenedAppoimentNotes both read tableRowLead.lead_id
                // for the archive POST URL. A stale tableRowLead would archive the wrong lead.
                // `followLead` (from useSelector line 45) is the lead loaded for this popup
                // session — has .id (lead PK) and .lead_treatments[].
                // dispatch(tableLead(followLead)) is synchronous — store is updated before
                // the thunk executes and reads store.getState().lead.tableRowLead.
                if (!followLead?.id) {
                    dispatch(updateError({
                        message: 'Cannot archive: lead not identified. Please close and reopen.',
                        success: false,
                    }));
                    return;
                }
                dispatch(tableLead(followLead));
                if (attendanceRecorded) {
                    // Attendance already recorded by AttendanceGate; only the archive POST needed.
                    dispatch(attenedAppoimentNotes(practiceId, id, handleCloseAndReload));
                } else {
                    // Full flow: record attendance first, then archive inside attenedAppoimentNotes.
                    dispatch(RecordAttended(handleCloseAndReload));
                }
            }
        } else {
            handleClose();
        }
    };

    const onSubmit = async (data) => {
        // Checklist activity (taskActivityDone) or a recorded attendance
        // (attendanceRecorded) satisfies the gate without a visibly-selected
        // outcome chip — see submitOutcome above.
        if (!activeTab && !taskActivityDone && !attendanceRecorded) {
            dispatch(updateError({ message: 'Please select any one Tab', success: false }));
            return;
        }
        const callAppointmentNote = JSON.stringify(data) !== JSON.stringify(defaultValues);
        appointmentNotesUpdate(data, callAppointmentNote);
    };

    const handleNext = async () => {
        // V2 guided-checklist "Reminder & Follow-up" outcome (with-template →
        // v2GuidedActive, OR no-template → templatesEmpty showing
        // ScheduleTaskReminder) persists its tasks + reminders INLINE. That
        // outcome renders the V2 guided checklist (no attendedRef), so the
        // form-submit path — handleSubmit(onSubmit) → appointmentNotesUpdate →
        // attendedRef.current.submit('finish') — has no submittable ref and
        // never closes (the V1 FollowUp ref only exists when tasks-v2 is OFF).
        // Finish here only needs to close + reload the leads table.
        const isV2ReminderOutcome =
            submitOutcome === 'Reminder & Follow-up'
            && hasFeatureAccess('tasks-v2', practiceId)
            && (v2GuidedActive || templatesEmpty);
        if (isV2ReminderOutcome) {
            if (typeof handleCloseAndReload === 'function') handleCloseAndReload();
            else {
                if (typeof handleReloadLeads === 'function') handleReloadLeads();
                handleClose();
            }
            return;
        }
        handleSubmit(onSubmit)();
    };

    // Footer "Treatment complete" → open the irreversible-close confirmation dialog.
    // The actual POST happens in handleConfirmTreatmentComplete (the dialog's
    // Confirm button), NOT on this click — treatment-close can't be undone.
    const handleTreatmentComplete = () => {
        setCompleteConfirmOpen(true);
    };

    // Confirm → close the treatment via the REAL CompleteTreatment thunk (the same
    // one the treatment-started hub uses; POST
    // /leads/{lid}/treatments/{ltid}/treatment_closed/) and then close + reload.
    //
    // We do NOT route through the TreatmentComplete tab's submit('finish') path:
    // that only dispatches updateCompleteStatus(true) and relies on a host listener
    // with NO treatment-complete branch — so a bare treatment-complete outcome never
    // POSTs nor closes (the dead path the footer move exposed). Dispatching
    // CompleteTreatment directly is the working path.
    const handleConfirmTreatmentComplete = () => {
        const completeLeadId = v2LeadId;
        const treatmentId =
            currentTreatmentDataGlobal?.treatmentData?.id
            ?? followLead?.lead_treatments?.[0]?.id
            ?? null;
        if (!completeLeadId || !treatmentId) {
            dispatch(updateError({ success: false, message: 'Could not resolve the treatment to complete.' }));
            setCompleteConfirmOpen(false);
            return;
        }
        setCompleteSubmitting(true);
        const price = resolveLeadTreatmentPrice({ followLead });
        const data = {
            treatment: getSelectedTreatment() || '',
            custom_price: price ?? '',
        };
        const onClose = () => {
            setCompleteSubmitting(false);
            setCompleteConfirmOpen(false);
            if (typeof handleCloseAndReload === 'function') handleCloseAndReload();
            else {
                if (typeof handleReloadLeads === 'function') handleReloadLeads();
                handleClose();
            }
        };
        dispatch(CompleteTreatment(data, completeLeadId, treatmentId, practiceId, onClose));
    };

    useImperativeHandle(
        ref, () => ({
            async submit(status) {
                await handleSubmit((data) => onSubmit(data, status))();
            },
        }),
        [activeTab]
    );

    const getCurrentTreatmentData = () => {
        if (isOverview) {
            return { selectedTreatmentData: currentTreatmentDataGlobal };
        }
        return {};
    };

    // Reminder & Follow-up chip content.
    //
    // V2 practices with a template applied → show the V2 guided checklist
    //   (AttendedGuidedChecklistV2 / AttendedGuidedChecklist) for the task-display
    //   portion; the new ScheduleTaskReminder is appended below the checklist so
    //   users can also create a standalone follow-up task when no checklist action
    //   is outstanding.
    //
    // V2 practices with NO template applied (v2GuidedActive is false because
    //   templatesEmpty) → show ScheduleTaskReminder directly (fixes the
    //   "blank Reminder & Follow-up tab" on lead 152413).
    //
    // Non-V2 practices → unchanged V1 FollowUpChecklistV2 fallback.
    // Checklist activity satisfies Finish WITHOUT revealing the picker or
    // highlighting the yellow Reminder & Follow-up card: set a flag only,
    // never setActiveTab (which would un-hide the picker via its !activeTab gate).
    const onTaskActivity = () => {
        setTaskActivityDone(true);
    };

    // Reminder-component consolidation (Phase 2): when tasks-v2 is active with an
    // applied template, render the SAME shared <ScheduleReminderTaskSection> the
    // FollowUpV2 and Attended Consultation popups use, so this BOOKED→ATTENDED popup
    // gains standalone/ad-hoc tasks, the pause/complete affordances, the V2 action
    // buttons and the creation/completion animations — instead of the template-only
    // AttendedGuidedChecklistV2 path.
    //
    // The AttendanceGate is unaffected: it is rendered by the shell as `gateNode`
    // (flowStep === 'ATTENDANCE_GATE') and the reminder body only shows AFTER the
    // gate is dismissed. checklistRefreshKey bumps the shared hook's cache after
    // attendance is recorded so task due dates recompute (the BE only computes them
    // once the lead flips to CONSULTATION_ATTENDED).
    //
    // The hook is invoked unconditionally (hooks can't be conditional). It wraps
    // useV2ChecklistAutocomplete keyed by practiceId:leadId.
    const sharedChecklist = useLeadReminderTasks({ practiceId, leadId: v2LeadId });

    // Refetch the shared task list once attendance is recorded — task due dates are
    // only computed by the BE after the lead flips to CONSULTATION_ATTENDED.
    useEffect(() => {
        if (checklistRefreshKey > 0) sharedChecklist.refresh?.({ bustCache: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checklistRefreshKey]);

    // Bridge: the shared section drives task activity itself, but AA's Finish gate
    // keys off taskActivityDone. Mark it done once the lead has any task so Finish is
    // enabled (matches the previous onTaskActivity semantics).
    useEffect(() => {
        if (v2GuidedActive && (sharedChecklist.items?.length ?? 0) > 0) {
            setTaskActivityDone(true);
        }
    }, [v2GuidedActive, sharedChecklist.items]);

    // Phase 2: useReminderContent still serves the non-v2 and no-template branches
    // (V1 FollowUpChecklistV2 fallback / ScheduleTaskReminder). AA passes mode
    // 'gate-first' and the AA-only gate props. The v2-with-template branch is swapped
    // to ScheduleReminderTaskSection below.
    // Active treatment being acted on — scopes custom-reminder create + pause and
    // the V2 task list. Shared by BOTH the legacy (useReminderContent) and the V2
    // (ScheduleReminderTaskSection) branches so they can't drift apart: the V2
    // branch previously omitted this, leaking other treatments' tasks on
    // multi-treatment leads. Mirrors ConsultationAttendedAppointment.jsx.
    const activeLeadTreatmentId = currentTreatmentDataGlobal?.treatmentData?.id
        ?? (followLead?.lead_treatments || []).find((t) => t.status === 'CONSULTATION_BOOKED')?.id
        ?? followLead?.lead_treatments?.[0]?.id
        ?? null;

    const handleExplicitArchiveSubmit = async ({ data, openCommunication }) => {
        const archiveLeadId = followLead?.id || user?.lead_id || user?.id;
        if (!archiveLeadId || !practiceId) {
            dispatch(updateError({ success: false, message: 'Cannot archive: lead not identified. Please close and reopen.' }));
            return;
        }
        await dispatch(beginArchiveLead({
            leadId: archiveLeadId,
            practiceId,
            leadTreatmentId: activeLeadTreatmentId,
            reason: data?.archiveReason || '',
            note: data?.archiveNotes || '',
            isOpenForCommunication: openCommunication?.is_open_for_communication,
            source: 'attended-popup:archive',
            handleClose,
        }));
        handleReloadLeads?.();
    };

    const legacyReminderTabComponent = useReminderContent({
        practiceId,
        leadId: v2LeadId,
        followLead,
        v2GuidedActive,
        useV2Guided,
        showCustomReminder,
        setShowCustomReminder,
        onTaskActivity,
        handleClose,
        handleReloadLeads,
        onOpenSidebarTab,
        leadTreatmentId: activeLeadTreatmentId,
        mode: 'gate-first',
        V1Fallback: (
            <FollowUpChecklistV2
                practiceId={practiceId}
                leadId={followLead?.id || user?.lead_id || user?.id}
                leadDetail={followLead || { id: user?.lead_id || user?.id }}
                handleClose={handleClose}
            />
        ),
        dbgSx,
        DbgTag,
        // AA-only gate props
        gateRenderedInHost: true,
        externalGateDismissed: gateDismissed,
        externalRefreshKey: checklistRefreshKey,
        handleNoAttended,
    });

    const reminderTabComponent = v2GuidedActive ? (
        <ScheduleReminderTaskSection
            key={v2LeadId}
            practiceId={practiceId}
            leadId={v2LeadId}
            leadDetail={followLead}
            leadTreatmentId={activeLeadTreatmentId}
            onOpenSidebarTab={onOpenSidebarTab}
            onReloadLeads={handleReloadLeads}
            onClose={handleClose}
            checklist={sharedChecklist}
            renderHero
            debugSource={`${debugSource}:attended-appointment`}
            dbgSx={dbgSx}
            DbgTag={DbgTag}
        />
    ) : legacyReminderTabComponent;

    // Treatment label shown on the "Starting treatment" chip and the "Treatment
    // complete" footer button when the lead has more than one treatment.
    //
    // Both actions target the SAME treatment resolved by the popup:
    //   - "Starting treatment" → NewAppointment → getTreatmentData() → lead_treatments[0]
    //     sorted by updated_at DESC (same as getSelectedTreatment fallback below)
    //   - "Treatment complete" → handleConfirmTreatmentComplete → currentTreatmentDataGlobal
    //     ?.treatmentData?.id ?? lead_treatments[0].id (same resolution)
    //
    // For isOverview the target is currentTreatmentDataGlobal.treatmentData; for the
    // dashboard table popup it is lead_treatments[0] sorted by updated_at DESC.
    // Mirror that resolution exactly so the label is always truthful.
    //
    // Only non-null when >1 treatment — single-treatment leads show no label
    // (zero layout change for the common case).
    //
    // MUST be declared before PROFILE_TABS (which references it at the
    // "Starting treatment" chip label) to avoid a TDZ ReferenceError.
    const allLeadTreatments = followLead?.lead_treatments || [];
    const actionTreatmentLabel = (() => {
        if (allLeadTreatments.length <= 1) return null;
        // Resolved target treatment — mirrors getTreatmentData() in NewAppointment and
        // handleConfirmTreatmentComplete in this file.
        const targetTx = isOverview && currentTreatmentDataGlobal?.treatmentData
            ? currentTreatmentDataGlobal.treatmentData
            : allLeadTreatments.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
        return targetTx?.treatment_data?.name
            || targetTx?.treatment_data?.treatment_item?.name
            || targetTx?.treatment
            || null;
    })();

    const PROFILE_TABS = [
        {
            // Leftmost chip — opens the V2 Guided Checklist (or V1 FollowUpChecklistV2 fallback
            // when the tasks-v2 feature flag is off / templates are empty).
            value: 'Reminder & Follow-up',
            icon: <Iconify icon={'solar:bell-bold-duotone'} width={20} height={20} />,
            component: reminderTabComponent,
        },
        {
            value: 'Starting treatment',
            // When the lead has >1 treatment, append the target treatment name so
            // reps can see which treatment the action will actually operate on.
            // Single-treatment leads: label is undefined → falls back to value (unchanged).
            label: actionTreatmentLabel ? `Starting treatment · ${actionTreatmentLabel}` : undefined,
            icon: <Iconify icon={'solar:medical-kit-bold-duotone'} width={20} height={20} />,
            component: <NewAppointment {...getCurrentTreatmentData()} ref={attendedRef} type={activeTab} popupType={type} rowId={id} handleClose={handleClose} handleReloadLeads={handleReloadLeads} />,
        },
        {
            value: 'Archive',
            icon: <Iconify icon={'eva:archive-fill'} width={20} height={20} />,
            component: <Archive ref={attendedRef} handleClose={handleClose} screenType={type} handleReloadLeads={handleReloadLeads} onExplicitArchiveSubmit={handleExplicitArchiveSubmit} />,
        }
    ];

    // 'Treatment complete' is footer-only (not a picker chip) — it's mounted in the
    // shell's OUTCOME box via hiddenTabs when the footer button selects it, so its
    // submit ref (attendedRef) populates before handleTreatmentComplete fires.
    const TREATMENT_COMPLETE_TAB = {
        value: 'Treatment complete',
        icon: <Iconify icon={'akar-icons:circle-check-fill'} width={20} height={20} />,
        component: <TreatmentComplete ref={attendedRef} type={type} handleNext={handleNext} handleClose={handleClose} />,
    };

    const getAttendedComponents = () => {
        if (type === 'CreatedLead') {
            return PROFILE_TABS.filter(el => el.value !== 'Archive');
        }
        return PROFILE_TABS;
    };

    const getSelectedTreatment = () => {
        if (isOverview) return currentTreatmentDataGlobal?.treatmentData?.treatment || followLead?.lead_treatments?.[0]?.treatment;
        return followLead?.lead_treatments?.[0]?.treatment;
    };

    const getSelectedTreatmentDateTime = () => {
        const tempDate = followLead?.lead_appointment?.find((ele) => ele?.lead_treatment === currentTreatmentDataGlobal?.treatmentData?.id)?.datetime;
        if (isOverview && tempDate) return getTreatmentDate(tempDate) || '';
        return getTreatmentDate(followLeadFilterAttend?.[0]?.datetime) || '';
    };

    const getCurrentTreatmentAssignees = () => {
        if (isOverview) {
            const found = followLead?.lead_appointment?.find((ele) => ele?.lead_treatment === currentTreatmentDataGlobal?.treatmentData?.id);
            return found?.assignees || followLead?.lead_appointment?.[0]?.assignees;
        }
        return followLeadFilterAttend?.[0]?.assignees;
    };

    useEffect(() => {
        if (followLead?.assignees) {
            setAssigneeSelected(followLead.assignees || []);
        }
    }, [followLead]);

    // Auto-collapse info card when an outcome tab is selected
    useEffect(() => {
        if (activeTab) {
            setInfoExpanded(false);
        }
    }, [activeTab]);

    // Header slot — slim (gate-pending) vs full layout, chosen by the popup so the
    // exact compacted header + "Booked" chip layout is preserved.
    const headerNode = (user !== undefined && user && (gatePending ? (
                    // Slim, de-emphasised summary while the attendance question is pending.
                    <Box sx={{ px: 3, py: 1.25, mt: 1, mb: 2, bgcolor: '#ffffff', borderRadius: '12px', opacity: 0.7, ...dbgSx('#2962FF') }}>
                        <DbgTag color="#2962FF" label="HEADER" />
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                            <Chip
                                size="small"
                                icon={<Iconify icon="mdi:calendar-clock" width={13} height={13} sx={{ color: '#f59e0b !important', ml: '6px !important' }} />}
                                label="Booked"
                                sx={{
                                    height: 22,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    bgcolor: 'rgba(245, 158, 11, 0.10)',
                                    color: '#f59e0b',
                                    letterSpacing: '-0.005em',
                                    border: 'none',
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                            <Typography {...piiProps} sx={{ fontSize: '15px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.014em', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {capitalizeFirstLetter(followLead?.first_name || '')} {capitalizeFirstLetter(followLead?.last_name || '')}
                            </Typography>
                            {getSelectedTreatment() && (
                                <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                                    · {getSelectedTreatment()}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                ) : <Box sx={{ px: 3, py: 2, mt: 1, mb: 2, bgcolor: '#ffffff', borderRadius: '12px', ...dbgSx('#2962FF') }}>
                    <DbgTag color="#2962FF" label="HEADER" />
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:calendar-clock" width={13} height={13} sx={{ color: '#f59e0b !important', ml: '6px !important' }} />}
                                    label="Booked"
                                    sx={{
                                        height: 22,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        bgcolor: 'rgba(245, 158, 11, 0.10)',
                                        color: '#f59e0b',
                                        letterSpacing: '-0.005em',
                                        border: 'none',
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:tooth-outline" width={13} height={13} sx={{ color: '#078DEE !important', ml: '6px !important' }} />}
                                    label={getSelectedTreatment() || '—'}
                                    sx={{
                                        height: 24, fontSize: '12px', fontWeight: 500,
                                        bgcolor: 'rgba(7, 141, 238, 0.08)', color: '#078DEE',
                                        border: 'none', '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:calendar-clock" width={13} height={13} sx={{ color: '#00A76F !important', ml: '6px !important' }} />}
                                    label={getSelectedTreatmentDateTime(followLeadFilterAttend) || '—'}
                                    sx={{
                                        height: 24, fontSize: '12px', fontWeight: 500,
                                        bgcolor: 'rgba(0, 167, 111, 0.08)', color: '#00A76F',
                                        border: 'none', '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                                {(() => {
                                    const assignees = getCurrentTreatmentAssignees(followLeadFilterAttend);
                                    if (!assignees?.length) return null;
                                    return (
                                        <Tooltip title={`Practitioner: ${assignees.map(a => `${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`.trim()).filter(Boolean).join(', ')}`} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                            <Stack direction="row" alignItems="center" spacing={-0.5} sx={{ ml: 0.5 }}>
                                                {assignees.slice(0, 2).map((a, i) => (
                                                    <AssignUserAvatar
                                                        key={i}
                                                        sx={{ height: 22, width: 22, fontSize: '10px', border: '1.5px solid white' }}
                                                        src={a?.user?.avatar || ''}
                                                        name={`${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`}
                                                        role={a?.role}
                                                    />
                                                ))}
                                            </Stack>
                                        </Tooltip>
                                    );
                                })()}
                                {assigneeSelected?.length > 0 && (
                                    <Tooltip title={`Assignees: ${assigneeSelected.map(a => `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim()).filter(Boolean).join(', ')}`} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                        <Stack direction="row" alignItems="center" spacing={-0.5}>
                                            {assigneeSelected.slice(0, 2).map((ele, i) => (
                                                <AssignUserAvatar
                                                    key={i}
                                                    sx={{ height: 22, width: 22, fontSize: '10px', border: '1.5px solid white' }}
                                                    src={ele.user?.avatar || ''}
                                                    name={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`}
                                                    role={ele?.role}
                                                />
                                            ))}
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Stack>
                            <Typography {...piiProps} sx={{ fontSize: '24px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.026em', lineHeight: 1.15, mt: 0.75 }}>
                                {capitalizeFirstLetter(followLead?.first_name || '')} {capitalizeFirstLetter(followLead?.last_name || '')}
                            </Typography>
                        </Box>
                        <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ flexShrink: 0 }}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '0.02em', textTransform: 'uppercase', fontWeight: 500, mb: 0.25 }}>
                                    Price
                                </Typography>
                                <Typography data-testid="attended-treatment-price" sx={{ fontSize: '24px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.026em', lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                                    {(() => {
                                        const price = resolveLeadTreatmentPrice({ followLead });
                                        if (price == null) return '—';
                                        return `£${Number(price).toLocaleString('en-GB')}`;
                                    })()}
                                </Typography>
                            </Box>
                            <Stack direction="column" spacing={0.25} sx={{ ml: 0.5 }}>
                                <IconButton
                                    size="small"
                                    onClick={() => editDetailHandler()}
                                    sx={{ color: '#6e6e73', '&:hover': { bgcolor: 'rgba(0,113,227,0.06)', color: '#0071e3' } }}
                                >
                                    <Iconify icon="solar:pen-bold" width={15} height={15} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => setInfoExpanded(!infoExpanded)}
                                    sx={{ color: '#6e6e73', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#1d1d1f' } }}
                                >
                                    <Iconify icon={infoExpanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={18} height={18} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                    <Collapse in={infoExpanded} timeout={300}>
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ height: '0.5px', bgcolor: 'rgba(0,0,0,0.10)', mb: 2 }} />
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                {(followLead?.phone || followLead?.lead_contact?.phone) && (
                                    <Typography {...piiProps} sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                                        {followLead?.phone || followLead?.lead_contact?.phone}
                                    </Typography>
                                )}
                                {followLead?.lead_address && (
                                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                                        · {[followLead?.lead_address?.address_line_1, followLead?.lead_address?.city, followLead?.lead_address?.postcode].filter(Boolean).join(', ')}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ gap: 4 }}>
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em' }}>Practitioner</Typography>
                                        <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => OpenAssignTask(user, getCurrentTreatmentAssignees(followLeadFilterAttend), e)}>
                                            <Iconify width={12} height={12} icon={'solar:pen-bold'} sx={{ color: '#6e6e73' }} />
                                        </IconButton>
                                    </Stack>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                                        {(() => {
                                            const assignees = getCurrentTreatmentAssignees(followLeadFilterAttend);
                                            const names = assignees?.map(a => `${a?.user?.first_name || a?.first_name || ''} ${a?.user?.last_name || a?.last_name || ''}`.trim()).filter(Boolean) || [];
                                            if (names.length === 0) return '—';
                                            if (names.length === 1) return names[0];
                                            return `${names[0]} + ${names.length - 1}`;
                                        })()}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.005em', mb: 0.5 }}>Assignees</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {assigneeSelected?.length > 0 ? assigneeSelected.slice(0, 4).map((ele, index) => (
                                            <Tooltip key={index} title={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                                <AssignUserAvatar
                                                    sx={{ height: 28, width: 28, fontSize: '11px' }}
                                                    src={ele.user?.avatar || ''}
                                                    name={`${ele.user?.first_name || ''} ${ele.user?.last_name || ''}`}
                                                    role={ele?.role}
                                                />
                                            </Tooltip>
                                        )) : <Typography sx={{ fontSize: '13px', color: '#6e6e73' }}>—</Typography>}
                                        {assigneeSelected?.length > 4 && (
                                            <Typography sx={{ fontSize: '11px', color: '#6e6e73', ml: 0.5 }}>+{assigneeSelected.length - 4}</Typography>
                                        )}
                                        <AssignedUsersShow assigneeSelected={assigneeSelected} setAssigneeSelected={setAssigneeSelected} leadId={followLead?.id || user?.lead_id || user?.id} compact />
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Collapse>
                </Box>));

    // Attendance-gate slot (AA only). The shell renders it only when
    // flowStep === 'ATTENDANCE_GATE', so this is the inner block without that guard.
    const gateNode = (
                    <Collapse in={flowStep === 'ATTENDANCE_GATE'} timeout={320} unmountOnExit>
                        <Box
                            sx={{
                                width: '100%',
                                my: 3,
                                bgcolor: '#ffffff',
                                borderRadius: '16px',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                                p: { xs: 3, sm: 4 },
                                ...dbgSx('#00C853'),
                            }}
                        >
                            <DbgTag color="#00C853" label="ATTENDANCE GATE" />
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:tooth-outline" width={12} height={12} sx={{ color: '#078DEE !important', ml: '6px !important' }} />}
                                    label={getSelectedTreatment() || '—'}
                                    sx={{
                                        height: 22, fontSize: '11px', fontWeight: 500,
                                        bgcolor: 'rgba(7, 141, 238, 0.08)', color: '#078DEE',
                                        border: 'none', '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                                <Chip
                                    size="small"
                                    icon={<Iconify icon="mdi:calendar-clock" width={12} height={12} sx={{ color: '#00A76F !important', ml: '6px !important' }} />}
                                    label={getSelectedTreatmentDateTime(followLeadFilterAttend) || '—'}
                                    sx={{
                                        height: 22, fontSize: '11px', fontWeight: 500,
                                        bgcolor: 'rgba(0, 167, 111, 0.08)', color: '#00A76F',
                                        border: 'none', '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </Stack>
                            <Typography {...piiProps} sx={{ fontSize: '24px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.026em', lineHeight: 1.15, mb: 2 }}>
                                {capitalizeFirstLetter(followLead?.first_name || '')} {capitalizeFirstLetter(followLead?.last_name || '')}
                            </Typography>
                            {currentTreatmentDataGlobal?.treatmentData?.treatment && (
                                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1, textAlign: 'center' }}>
                                    Treatment: <b>{currentTreatmentDataGlobal.treatmentData.treatment}</b>{currentTreatmentDataGlobal.treatmentData.status ? ` · ${String(currentTreatmentDataGlobal.treatmentData.status).replace(/_/g, ' ')}` : ''}
                                </Typography>
                            )}
                            <AttendanceGate
                                variant="inline-hero"
                                leadDetail={followLead}
                                appointmentSelection={outcomeSelection}
                                handleNoAttended={handleNoAttended}
                                onDismissed={() => { setGateDismissed(true); setAttendanceRecorded(true); setChecklistRefreshKey((k) => k + 1); }}
                            />
                        </Box>
                    </Collapse>
    );

    // Finish disabled — unchanged from the inline footer: no outcome (submitOutcome
    // null) or payment pending disable Finish. The ClinCheck-status arg is now
    // null — the ClinCheck outcome tab was removed, so its status branch in
    // isDisabledTabForFinishButton is unreachable for the remaining outcomes.
    const finishDisabled = isDisabledTabForFinishButton(null, currentTab, submitOutcome, isPaymentMandatory, depositPendingStatus, type, treatmentLoading) || isLoader;

    // Derive resolved treatment + appointment using the same 3-step fallback chain
    // as RecordAttended (appointmentThunks.js) so the debug box reflects what the
    // thunk will actually target when the user clicks "Yes, attended".
    const _allTreatments = followLead?.lead_treatments || [];
    const _allAppts = followLead?.lead_appointment || [];
    const _resolvedTreatment = _allTreatments.find((t) => t.status === 'CONSULTATION_BOOKED') ?? _allTreatments[0] ?? null;
    const _treatmentAppts = _allAppts.filter((a) => a?.lead_treatment === _resolvedTreatment?.id);
    const _pool = _treatmentAppts.length > 0 ? _treatmentAppts : _allAppts;
    const _bookedAppt = _pool.find((a) => a?.status === 'BOOKED' || a?.status === 'CONSULTATION_BOOKED');
    // fallback 2: any booked appointment anywhere on the lead
    const _bookedApptFb2 = !_bookedAppt ? _allAppts.find((a) => a?.status === 'BOOKED' || a?.status === 'CONSULTATION_BOOKED') : null;
    const _resolvedAppt = _bookedAppt ?? _bookedApptFb2 ?? (_allAppts.length > 0 ? _allAppts[0] : null);
    let _apptFallbackStep = null;
    if (_bookedAppt) { _apptFallbackStep = 1; }
    else if (_bookedApptFb2) { _apptFallbackStep = 2; }
    else if (_allAppts.length > 0) { _apptFallbackStep = 3; }

    const debugStateObject = {
        popup: 'AttendedAppointment',
        practiceId,
        leadId: v2LeadId,
        leadName: followLead ? `${followLead.first_name ?? ''} ${followLead.last_name ?? ''}`.trim() : null,
        treatments: _allTreatments.map((t) => ({ id: t.id, treatment: t.treatment, status: t.status })),
        resolvedTreatment: _resolvedTreatment
            ? { id: _resolvedTreatment.id, treatment: _resolvedTreatment.treatment, status: _resolvedTreatment.status }
            : null,
        resolvedAppointment: _resolvedAppt
            ? { id: _resolvedAppt.id, datetime: _resolvedAppt.datetime, status: _resolvedAppt.status }
            : null,
        appointmentFallbackStep: _apptFallbackStep,
        // --- internal flow state below ---
        isLeadBooked,
        tasksV2Feature: hasFeatureAccess('tasks-v2', practiceId),
        gcInitialLoading,
        templatesEmpty,
        v2GuidedActive,
        guidedParam,
        useV2Guided,
        gateDismissed,
        flowStep,
        activeTab,
        submitOutcome,
        taskActivityDone,
        attendanceRecorded,
        checklistRefreshKey,
    };

    return (
        <>
            <AttendedPopupShell
                practiceId={practiceId}
                flowStep={flowStep}
                activeTab={activeTab}
                onActiveTabChange={setActiveTab}
                tabs={getAttendedComponents()}
                hiddenTabs={[TREATMENT_COMPLETE_TAB]}
                onTreatmentComplete={handleTreatmentComplete}
                treatmentCompleteLoading={isLoader || completeSubmitting}
                showTreatmentCompleteButton={isFinishButtonAvailable && flowStep !== 'ATTENDANCE_GATE'}
                actionTreatmentLabel={actionTreatmentLabel}
                showSimilarLeadWarning={type !== 'CreatedLead'}
                tcoNode={<TCOScript handleClose={handleClose} handleNext={onSubmit} ref={attendedRef} />}
                methods={methods}
                onFormSubmit={handleSubmit(onSubmit)}
                headerNode={headerNode}
                gateNode={gateNode}
                reminderNode={reminderTabComponent}
                onFinish={handleNext}
                finishLoading={isLoader}
                finishDisabled={finishDisabled}
                isFinishAvailable={isFinishButtonAvailable}
                isInitiallyLoading={isInitiallyLoading}
                themeStretch={settings.themeStretch}
                handleClose={handleClose}
                debugStateObject={debugStateObject}
                attDebug={attDebug}
                dbgSx={dbgSx}
                DbgTag={DbgTag}
            />
            <CompleteTreatmentConfirmDialog
                open={completeConfirmOpen}
                submitting={completeSubmitting}
                onCancel={() => { setCompleteConfirmOpen(false); setCompleteSubmitting(false); }}
                onConfirm={handleConfirmTreatmentComplete}
            />
            {/* Superadmin-only debug overlay — mirrors ContactPopupDebugBox pattern.
                Surfaces the resolved treatment + appointment and the fallback step so
                Raj can share a screenshot when debugging multi-treatment attend issues.
                Self-gated on user.is_superuser; renders nothing for non-superadmins. */}
            <ContactPopupDebugBox
                popup="AttendedAppointment"
                debugSource={debugSource}
                practiceId={practiceId}
                leadId={v2LeadId}
                extra={{
                    leadName: debugStateObject.leadName,
                    treatments: debugStateObject.treatments,
                    resolvedTreatment: debugStateObject.resolvedTreatment,
                    resolvedAppointment: debugStateObject.resolvedAppointment,
                    appointmentFallbackStep: debugStateObject.appointmentFallbackStep,
                    flowStep: debugStateObject.flowStep,
                    isLeadBooked: debugStateObject.isLeadBooked,
                    attendanceRecorded: debugStateObject.attendanceRecorded,
                    submitOutcome: debugStateObject.submitOutcome,
                }}
            />
        </>
    );
});


AttendedAppointment.propTypes = {
    type: PropTypes.string,
    id: PropTypes.number,
    handleClose: PropTypes.func,
    handleCloseAndReload: PropTypes.func,
    user: PropTypes.object,
    OpenAssignTask: PropTypes.func,
    editDetailHandler: PropTypes.func,
    handleReloadLeads: PropTypes.func,
    isFinishButtonAvailable: PropTypes.bool,
    isOverview: PropTypes.bool,
    handleNoAttended: PropTypes.func,
    /** Switches the host popup's right sidebar tab (e.g. 'email' / 'whatsapp') — used by V2 follow-up. */
    onOpenSidebarTab: PropTypes.func,
    /** Where this popup was opened from — surfaced in the superadmin debug overlay. */
    debugSource: PropTypes.string,
};

export default AttendedAppointment;
